
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useGame } from '../../contexts/GameContext';
import { CheckCircle2, Circle, Target, Trophy } from "lucide-react";

const CareerObjectives: React.FC = () => {
  const { selectedRole, careerLevel, experience, yearsPassed, kpis } = useGame();
  
  if (!selectedRole) return null;
  
  // Find current and next career paths
  const currentPathIndex = selectedRole.careerPath.findIndex(path => path.level === careerLevel);
  const currentPath = selectedRole.careerPath[currentPathIndex];
  const nextPath = selectedRole.careerPath[currentPathIndex + 1];
  
  // Calculate progress percentages
  const experienceProgress = nextPath ? Math.min(100, (experience / nextPath.requiredExperience) * 100) : 100;
  const yearsProgress = nextPath ? Math.min(100, (yearsPassed / nextPath.requiredYears) * 100) : 100;
  
  const objectiveStatus = selectedRole.careerObjectives.map(objective => {
    // @ts-ignore - we know objective.target.kpi is a valid key
    const currentValue = kpis[objective.target.kpi];
    const targetValue = objective.target.value;
    
    // Higher is better for all KPIs except energyCost
    const isCompleted = objective.target.kpi === 'energyCost'
      ? currentValue <= targetValue
      : currentValue >= targetValue;
      
    // Calculate progress percentage (cap at 100%)
    let progressPercentage;
    if (objective.target.kpi === 'energyCost') {
      // For energy cost, lower is better
      const startValue = 0.10; // Assume starting at $0.10/kWh
      progressPercentage = Math.min(100, Math.max(0, 
        ((startValue - currentValue) / (startValue - targetValue)) * 100
      ));
    } else {
      // For other KPIs, higher is better
      const startValue = 0; // Assume starting at 0
      progressPercentage = Math.min(100, Math.max(0, 
        ((currentValue - startValue) / (targetValue - startValue)) * 100
      ));
    }
    
    return {
      ...objective,
      isCompleted,
      progressPercentage,
      currentValue
    };
  });

  return (
    <Card className="bg-web3-blue/10 border-web3-blue/20">
      <CardHeader>
        <CardTitle className="flex items-center">
          <Target className="mr-2 text-web3-green" /> Career Objectives
        </CardTitle>
        <CardDescription>Complete these objectives to advance your career</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-8">
          {/* Career Level Progress */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-md font-medium text-white">
                Next Promotion: {nextPath ? nextPath.title : 'Max Level Reached'}
              </h3>
              {!nextPath && (
                <div className="px-2 py-1 text-xs bg-web3-green/20 text-web3-green rounded-full">
                  Maximum Level
                </div>
              )}
            </div>
            
            {nextPath && (
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span>Experience: {experience}/{nextPath.requiredExperience} XP</span>
                    <span>{Math.round(experienceProgress)}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div className="bg-web3-green h-2 rounded-full" style={{ width: `${experienceProgress}%` }}></div>
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span>Years: {yearsPassed}/{nextPath.requiredYears} years</span>
                    <span>{Math.round(yearsProgress)}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div className="bg-web3-green h-2 rounded-full" style={{ width: `${yearsProgress}%` }}></div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Career Objectives */}
          <div>
            <h3 className="text-md font-medium text-white mb-3 flex items-center">
              <Trophy className="h-4 w-4 mr-2 text-web3-green" /> Role-Specific Objectives
            </h3>
            
            <div className="space-y-4">
              {objectiveStatus.map((objective, index) => (
                <div key={index} className={`border p-3 rounded-md ${
                  objective.isCompleted ? 'border-web3-green bg-web3-green/5' : 'border-web3-blue/20 bg-web3-blue/5'
                }`}>
                  <div className="flex items-start">
                    <div className="mt-1 mr-3">
                      {objective.isCompleted 
                        ? <CheckCircle2 className="h-5 w-5 text-web3-green" /> 
                        : <Circle className="h-5 w-5 text-muted-foreground" />}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-white">{objective.title}</h4>
                      <p className="text-sm text-white/80 mb-2">{objective.description}</p>
                      
                      <div>
                        <div className="flex justify-between text-xs mb-1">
                          <span>
                            Current: {
                              objective.target.kpi === 'energyCost' 
                                ? `$${kpis[objective.target.kpi].toFixed(3)}/kWh` 
                                : `${kpis[objective.target.kpi].toFixed(1)}%`
                            }
                          </span>
                          <span>
                            Target: {
                              objective.target.kpi === 'energyCost' 
                                ? `$${objective.target.value.toFixed(3)}/kWh` 
                                : `${objective.target.value}%`
                            }
                          </span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${objective.isCompleted ? 'bg-web3-green' : 'bg-web3-blue'}`} 
                            style={{ width: `${objective.progressPercentage}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      {objective.isCompleted && (
                        <div className="mt-2 text-xs bg-web3-green/20 text-web3-green px-2 py-1 rounded inline-block">
                          Completed! Reward: {objective.reward}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CareerObjectives;
